import { Dispatch, SetStateAction, createContext, useMemo, useState } from 'react';

import { deleteS3Item, listS3Items, uploadS3Item, viewS3Items } from 'lib/bucket';
import { curryCognitoCreds, curryCognitoCreds2 } from 'lib/cognito';

export type S3Item = {
  id: number;
  key: string;
  url: string;
};

type BucketProps = {
  children: React.ReactNode;
};

export type Bucket = {
  listItems: any;
  urls: S3Item[];
  urlsSet: Dispatch<SetStateAction<S3Item[]>>;
  getShopGalleryItems: (prefix: string, asPromise?: boolean) => any;
  viewItems: (key: string[]) => void;
  sendItem: (img: Array<FileList | null>, path: string) => any;
  deleteItem: (key: string) => any;
  getUrlWithoutCreds: Function;
  getListItems: Function;
  getUrl: Function;
  // Elements are really `File` (Actions.tsx spreads a FileList into
  // File[]), typed as FileList here to match `sendItem`'s existing
  // `Array<FileList | null>` signature (mirrors colmar-admin-catalogue-fe's
  // identical BucketContext.tsx).
  photosFromInput: FileList[] | null;
  setPhotosFromInput: any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

const defaultState: Bucket = {
  listItems: [],
  urls: [],
  urlsSet: () => null,
  getShopGalleryItems: () => null,
  viewItems: () => null,
  sendItem: () => null,
  deleteItem: () => null,
  getListItems: () => null,
  getUrl: () => null,
  getUrlWithoutCreds: () => null,
  photosFromInput: null,
  setPhotosFromInput: () => null,
};

export const BucketContext = createContext(defaultState);

// viewS3Items signs URLs for 1 h. Reusing a signed URL for 50 min lets remounts
// (tab switches, list re-renders, product + colour of the same image) hit the
// browser cache instead of re-signing and re-downloading. The promise is cached
// so concurrent requests for one key share it. Uploads/deletes clear the cache.
const SIGNED_URL_TTL_MS = 50 * 60 * 1000;
const signedUrlCache = new Map<string, { expiresAt: number; item: Promise<S3Item> }>();

export const BucketProvider: React.FunctionComponent<BucketProps> = ({ children }) => {
  const [listItems, listItemsSet] = useState<string[]>([]);
  const [urls, urlsSet] = useState<S3Item[]>([]);
  const [photosFromInput, setPhotosFromInput] = useState<any[] | null>(null); // eslint-disable-line @typescript-eslint/no-explicit-any

  const cognitoCreds = useMemo(() => curryCognitoCreds2(), []);

  // FIX: must actually CALL curryCognitoCreds(), not pass the function itself
  const cachedCreds = useMemo(() => curryCognitoCreds(), []);

  async function sendItem(img: Array<FileList | null>, path: string) {
    signedUrlCache.clear();
    const withCognitoCreds = await cachedCreds;
    const upload = withCognitoCreds(uploadS3Item);

    return upload(img, path);
  }

  async function viewItems(keys: string[]) {
    urlsSet([]);

    const withCognitoCreds = await cachedCreds;
    const view = withCognitoCreds(viewS3Items);

    Promise.all(keys.map((key: string) => view(key))).then((res) => {
      if (res) {
        const resIntoObject = res?.map((item, index) => ({
          id: index,
          key: item.key,
          url: item.url,
        }));

        urlsSet(resIntoObject);
      }
    });
  }

  async function getListItems(prefix: string) {
    const withCognitoCreds = await cachedCreds;
    const list = withCognitoCreds(listS3Items);

    return list(prefix);
  }

  function getUrl(url: string): Promise<S3Item> {
    const cached = signedUrlCache.get(url);
    if (cached && cached.expiresAt > Date.now()) return cached.item;

    const item = cachedCreds.then((withCognitoCreds) => withCognitoCreds(viewS3Items)(url));
    signedUrlCache.set(url, { expiresAt: Date.now() + SIGNED_URL_TTL_MS, item });
    item.catch(() => signedUrlCache.delete(url));

    return item;
  }

  async function getUrlWithoutCreds(url: string) {
    const view = cognitoCreds(viewS3Items);
    return view(url);
  }

  async function getShopGalleryItems(prefix: string) {
    urlsSet([]);

    const withCognitoCreds = await cachedCreds;
    const list = withCognitoCreds(listS3Items);

    return list(prefix).then((res: any) => {
      if (res && res.Contents) {
        const resMapped = res?.Contents?.map((item: any) => item.Key);
        listItemsSet(resMapped);
        viewItems(resMapped);
      }
    });
  }

  async function deleteItem(key: string) {
    signedUrlCache.clear();
    const withCognitoCreds = await cachedCreds;
    const remove = withCognitoCreds(deleteS3Item);

    return remove(key);
  }

  const state: Bucket = useMemo(
    () => ({
      listItems,
      getShopGalleryItems,
      viewItems,
      sendItem,
      urls,
      urlsSet,
      deleteItem,
      getListItems,
      getUrl,
      getUrlWithoutCreds,
      photosFromInput,
      setPhotosFromInput,
    }),
    [listItems, urls, photosFromInput]
  );

  return <BucketContext.Provider value={state}>{children}</BucketContext.Provider>;
};
